import { StarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarRatingProps {
	/** Rating value from 0 to 5 */
	rating: number;
	/** Size of the stars */
	size?: 'sm' | 'md' | 'lg';
	/** Show the numeric rating next to stars */
	showNumeric?: boolean;
	/** Custom class name */
	className?: string;
	/** Whether the rating is clickable */
	clickable?: boolean;
	/** Callback when rating is clicked */
	onClick?: () => void;
}

const SIZE_CLASSES = {
	sm: 'size-3',
	md: 'size-3.5',
	lg: 'size-4'
} as const;

const TEXT_CLASSES = {
	sm: 'text-xs font-semibold',
	md: 'text-xs font-bold',
	lg: 'text-sm font-bold'
} as const;

type StarFill = 'full' | 'half' | 'empty';

interface StarProps {
	fill: StarFill;
	size: keyof typeof SIZE_CLASSES;
}

const Star = ({ fill, size }: StarProps) => {
	const sizeClass = SIZE_CLASSES[size];

	if (fill === 'empty') {
		return <StarIcon className={cn(sizeClass, 'fill-muted text-muted')} aria-hidden="true" />;
	}

	// Full and half stars use clip-path for efficient rendering
	return (
		<StarIcon
			className={cn(sizeClass, 'fill-amber-400 text-amber-400')}
			style={fill === 'half' ? { clipPath: 'inset(0 50% 0 0)' } : undefined}
			aria-hidden="true"
		/>
	);
};

const STAR_POSITIONS = [1, 2, 3, 4, 5] as const;

const getStarFill = (position: number, rating: number): StarFill => {
	if (rating >= position) return 'full';
	if (rating >= position - 0.5) return 'half';
	return 'empty';
};

const StarRating = ({
	rating,
	size = 'md',
	showNumeric = true,
	className,
	clickable = false,
	onClick
}: StarRatingProps) => {
	const clampedRating = Math.max(0, Math.min(5, rating));

	const stars = (
		<div className="flex items-center gap-0.5">
			{STAR_POSITIONS.map((position) => (
				<Star key={position} fill={getStarFill(position, clampedRating)} size={size} />
			))}
		</div>
	);

	const numeric = showNumeric && (
		<span
			className={cn(
				'text-foreground',
				TEXT_CLASSES[size],
				clickable && 'group-hover:text-amber-700 transition-colors'
			)}
		>
			{clampedRating.toFixed(1)}
		</span>
	);

	const content = (
		<>
			{stars}
			{numeric}
		</>
	);

	if (clickable) {
		return (
			<button
				type="button"
				className={cn(
					'inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-2 -m-2 rounded-md',
					'transition-all duration-200 ease-in-out hover:scale-105 focus:outline-none group',
					className
				)}
				onClick={onClick}
				data-testid="star-rating"
			>
				{content}
			</button>
		);
	}

	return (
		<span className={cn('inline-flex items-center gap-1.5', className)} data-testid="star-rating">
			{content}
		</span>
	);
};

export default StarRating;
